import React, { useState } from 'react'
import { Node, mergeAttributes } from '@tiptap/core'
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react'
import { MenuItem, Select, IconButton, Tooltip } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'

/**
 * Custom Tiptap node that renders a "placeholder chip" instead of the raw `{{key}}`
 * text. Clinical staff sees a small dropdown widget showing the field name (e.g.
 * "Patient Name"); they can change it to a different field or remove it without
 * ever touching the {{}} syntax.
 *
 * The node serialises to <placeholder data-key="..."></placeholder> in the
 * editor's HTML; we convert that to/from the canonical {{key}} text outside
 * the editor (toEditorHtml / fromEditorHtml).
 */
const PlaceholderNode = Node.create({
    name: 'placeholder',
    group: 'inline',
    inline: true,
    atom: true,

    addOptions() {
        return {
            placeholderOptions: [],
        }
    },

    addAttributes() {
        return {
            dataKey: {
                default: '',
                parseHTML: (el) => el.getAttribute('data-key') || '',
                renderHTML: (attrs) => ({ 'data-key': attrs.dataKey }),
            },
        }
    },

    parseHTML() {
        return [{ tag: 'placeholder' }]
    },

    renderHTML({ HTMLAttributes }) {
        // Atom inline nodes have no content hole — must NOT include trailing 0
        return ['placeholder', mergeAttributes(HTMLAttributes)]
    },

    addNodeView() {
        return ReactNodeViewRenderer(PlaceholderChip)
    },
})


function PlaceholderChip(props) {
    const node = props?.node
    const updateAttributes = props?.updateAttributes || (() => {})
    const deleteNode = props?.deleteNode || (() => {})
    const extension = props?.extension
    const options = extension?.options?.placeholderOptions || []
    const dataKey = (node && node.attrs && node.attrs.dataKey) || ''
    const known = options.find((o) => o.value === dataKey)
    const labelText = known ? known.label : (dataKey || 'Select…')
    const isUnknown = !known && !!dataKey

    const [open, setOpen] = useState(false)

    return (
        <NodeViewWrapper as="span"
            contentEditable={false}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 2,
                padding: '0 4px',
                margin: '0 2px',
                borderRadius: 4,
                backgroundColor: isUnknown ? '#ffe0e0' : '#e3f2fd',
                border: '1px solid ' + (isUnknown ? '#ef9a9a' : '#90caf9'),
                fontSize: '0.85em',
                lineHeight: 1.4,
                verticalAlign: 'baseline',
                cursor: 'pointer',
                userSelect: 'none',
            }}
        >
            <Select
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                value={dataKey || ''}
                onChange={(e) => updateAttributes({ dataKey: e.target.value })}
                variant="standard"
                disableUnderline
                renderValue={() => (
                    <span style={{ color: isUnknown ? '#c62828' : '#0d47a1', fontWeight: 500 }}>
                        {labelText}
                    </span>
                )}
                sx={{
                    '& .MuiSelect-select': { padding: '0 18px 0 0', minHeight: 0, fontSize: '0.85em' },
                    '& .MuiSelect-icon': { fontSize: '1em', right: 0, color: isUnknown ? '#c62828' : '#0d47a1' },
                }}
            >
                {options.filter(o => o.value).map((o) => (
                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: '0.85em' }}>
                        {o.label}
                    </MenuItem>
                ))}
            </Select>
            <Tooltip title="Remove placeholder">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteNode() }}
                    sx={{ padding: 0, marginLeft: '2px', color: isUnknown ? '#c62828' : '#0d47a1' }}>
                    <CloseIcon sx={{ fontSize: '0.85em' }} />
                </IconButton>
            </Tooltip>
        </NodeViewWrapper>
    )
}


// ---- helpers to convert between canonical `{{key}}` and editor `<placeholder>` HTML

const PLACEHOLDER_TEXT_RE = /\{\{\s*([a-zA-Z_][\w]*)\s*\}\}/g
const PLACEHOLDER_TAG_RE = /<placeholder\b[^>]*\bdata-key=["']?([a-zA-Z_][\w]*)["']?[^>]*>\s*<\/placeholder>/gi
// Tiptap's resizable table extension stores column widths as `colwidth="N,N,..."`
// pixel values on cells. xhtml2pdf doesn't understand colwidth — it needs CSS
// width on the cell. We convert on save so the resized columns persist into PDF.
const COLWIDTH_RE = /<(td|th)\b([^>]*?)\scolwidth="([0-9.,\s]+)"([^>]*)>/gi

export function toEditorHtml(canonicalHtml) {
    if (!canonicalHtml) return canonicalHtml || ''
    return canonicalHtml.replace(PLACEHOLDER_TEXT_RE, (_, key) =>
        `<placeholder data-key="${key}"></placeholder>`
    )
}

function _mergeWidthIntoStyle(beforeAttrs, afterAttrs, widthPx) {
    // First width entry covers the cell itself (additional values are for
    // colspanned cells that the cell straddles — uncommon enough to ignore).
    const w = parseFloat(String(widthPx).split(',')[0])
    if (!w || Number.isNaN(w)) return beforeAttrs + afterAttrs
    // xhtml2pdf doesn't honor px reliably for table cell widths — use pt.
    // 1 CSS px = 0.75 pt at the standard 96 DPI baseline.
    const widthPt = (w * 0.75).toFixed(1).replace(/\.0$/, '')
    const widthDecl = `width: ${widthPt}pt`
    const combined = beforeAttrs + afterAttrs
    if (/\bstyle\s*=\s*"/i.test(combined)) {
        return combined.replace(/\bstyle\s*=\s*"([^"]*)"/i, (_m, existing) => {
            const trimmed = existing.trim().replace(/;$/, '')
            const next = trimmed ? `${trimmed}; ${widthDecl}` : widthDecl
            return `style="${next}"`
        })
    }
    return `${combined} style="${widthDecl}"`
}

// Match TEXT outside of HTML tags: `>...content...<`. Used to limit whitespace
// preservation to text nodes so we don't accidentally rewrite attribute values.
const TEXT_NODE_RE = /(>)([^<]*)(<)/g

function _preserveMultiSpaces(text) {
    // Replace runs of 2+ spaces with nbsp+space alternation so they survive
    // HTML's whitespace collapsing in both Tiptap editor and xhtml2pdf renderer.
    return text.replace(/  +/g, (run) => ' '.repeat(run.length - 1) + ' ')
}

export function fromEditorHtml(editorHtml) {
    if (!editorHtml) return editorHtml || ''
    // 1. Bridge Tiptap's drag-resize colwidth → CSS style="width: Npt"
    let html = editorHtml.replace(COLWIDTH_RE, (_, tag, before, widths, after) => {
        const w = parseFloat(String(widths).split(',')[0])
        if (!w || Number.isNaN(w)) return `<${tag}${before} colwidth="${widths}"${after}>`
        return `<${tag}${before} colwidth="${widths}"${_mergeWidthIntoStyle('', after, widths)}>`
    })
    // 2. Preserve runs of multiple spaces inside text nodes only
    html = html.replace(TEXT_NODE_RE, (_, lt, text, rt) => lt + _preserveMultiSpaces(text) + rt)
    // 3. Convert <placeholder> chips back to canonical {{key}} text
    html = html.replace(PLACEHOLDER_TAG_RE, (_, key) => `{{${key}}}`)
    return html
}

export default PlaceholderNode
